<template>
  <fields-list
    :fields="fields"
    :editing-data="editingData"
    @update-field="updateField($event.value, $event.field)"
  />
</template>

<script>
import { listsMixin } from "@/mixins/listsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import FieldsList from "@/newComponents/StockManagement/FieldsList.vue";

export default {
  name: "MicroDuctMaterialFields",

  mixins: [listsMixin, objectManagementMixin],

  components: {
    FieldsList,
  },

  props: {
    /**
     * The specific fields values passed by the parent component
     */
    familySpecificFields: {
      type: Object,
      required: true
    },

    editingData: {
      type: Boolean,
      required: true,
    }
  },

  data() {
    return {
      networkTypeOptions: null,
      installationTypeOptions: null,
      colorProfileOptions: null,
      implantationTypeOptions: null
    };
  },

  /**
   * Executed when the component is mounted
   * It's a preemptive verification
   *
   */
  mounted() {
    this.loadNetworkTypeOptions();
    this.loadInstallationTypeOptions();
    this.loadImplantationTypeOptions();
    this.loadColorProfileOptions();
  },

  watch: {
    editingData() {
      this.requiredFieldsFilled();
    }
  },

  computed: {
    fields() {
      return [
        {
          label: this.$i18n.t("NrMicroDucts"),
          value: this.familySpecificFields.smud_microduct_count,
          varName: 'smud_microduct_count',
          inputType: 'number',
          disabled: false,
          required: true,
          min: '1',
          step: '1',
        },
        {
          label: this.$i18n.t("UsableCoreDuct"),
          value: !this.editingData
            ? this.familySpecificFields.smud_microduct_core ? this.$i18n.t('True') : this.$i18n.t('False')
            : this.familySpecificFields.smud_microduct_core,
          varName: 'smud_microduct_core',
          inputType: 'checkbox',
          disabled: false,
          required: true,
          checkboxOptions: [
            {
              id: 'usable-core-duct-checkbox',
              label: '',
            }
          ],
          checkboxIconStyle: { 'height': '11px', 'min-width': '11px' }
        },
        {
          label: this.$i18n.t("MicroDuctNetworkType"),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.microduct_network_type, this.networkTypeOptions)
            : this.familySpecificFields.microduct_network_type,
          varName: 'microduct_network_type',
          inputType: 'multiselect',
          disabled: false,
          required: true,
          options: this.networkTypeOptions,
          clearable: true,
          size: '100%',
        },
        {
          label: this.$i18n.t("InstallationType"),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.microduct_installation_type, this.installationTypeOptions)
            : this.familySpecificFields.microduct_installation_type,
          varName: 'microduct_installation_type',
          inputType: 'multiselect',
          disabled: false,
          required: true,
          options: this.installationTypeOptions,
          clearable: true,
          size: '100%',
        },
        {
          label: this.$i18n.t("PathwayInstallationType"),
          value: !this.editingData
            ? this.parseSelectValue(this.familySpecificFields.microduct_pathway_type, this.implantationTypeOptions)
            : this.familySpecificFields.microduct_pathway_type,
          varName: 'microduct_pathway_type',
          inputType: 'multiselect',
          disabled: false,
          required: true,
          options: this.implantationTypeOptions,
          clearable: false,
          size: '100%'
        },
        {
          label: this.$i18n.t("ColorProfile"),
          value: !this.editingData
            ? this.parseSelectValue([this.familySpecificFields.smud_microduct_color_profile], this.colorProfileOptions)
            : this.familySpecificFields.smud_microduct_color_profile,
          varName: 'smud_microduct_color_profile',
          inputType: 'select',
          disabled: false,
          required: true,
          options: this.colorProfileOptions,
          clearable: false,
          size: '100%'
        },
        {
          label: this.$i18n.t("SheathOuterDiameter"),
          value: this.familySpecificFields.smud_microduct_sheath_diameter_outer,
          varName: 'smud_microduct_sheath_diameter_outer',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("MicroDuctOuterDiameter"),
          value: this.familySpecificFields.smud_microduct_diameter_outer,
          varName: 'smud_microduct_diameter_outer',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("MicroDuctInnerDiameter"),
          value: this.familySpecificFields.smud_microduct_diameter_inner,
          varName: 'smud_microduct_diameter_inner',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("CoreOuterDiameter"),
          value: this.familySpecificFields.smud_microduct_core_diameter_outer,
          varName: 'smud_microduct_core_diameter_outer',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("CoreInnerDiameter"),
          value: this.familySpecificFields.smud_microduct_core_diameter_inner,
          varName: 'smud_microduct_core_diameter_inner',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("MicroDuctBendRadiusLoad"),
          value: this.familySpecificFields.smud_microduct_bend_radius_load,
          varName: 'smud_microduct_bend_radius_load',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("MicroDuctBendRadiusNoLoad"),
          value: this.familySpecificFields.smud_microduct_bend_radius_no_load,
          varName: 'smud_microduct_bend_radius_no_load',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("MicroDuctWeight"),
          value: this.familySpecificFields.smud_microduct_weight,
          varName: 'smud_microduct_weight',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },
        {
          label: this.$i18n.t("MicroDuctLength"),
          value: this.familySpecificFields.smud_microduct_length,
          varName: 'smud_microduct_length',
          inputType: 'number',
          disabled: false,
          min: '0.001',
          step: '0.001',
        },

      ]
    },
  },

  methods: {
    /**
     * Validations for the various required fields
     *
     * @return {boolean} indicating if the fields are valid
     */
    requiredFieldsValidation() {
      return (
        !!this.familySpecificFields.smud_microduct_count && !!this.familySpecificFields.smud_microduct_count >= 1 &&
        !!this.familySpecificFields.microduct_network_type && !!this.familySpecificFields.microduct_network_type.length > 0 &&
        !!this.familySpecificFields.microduct_installation_type && !!this.familySpecificFields.microduct_installation_type.length > 0 &&
        !!this.familySpecificFields.microduct_pathway_type && !!this.familySpecificFields.microduct_pathway_type.length > 0 &&
        !!this.familySpecificFields.smud_microduct_color_profile && !!this.familySpecificFields.smud_microduct_color_profile.length > 0
      );
    },

    /**
     * Loads the different types of cable options and assigns them to the attribute networkTypeOptions
     */
    async loadNetworkTypeOptions() {
      try {
        this.networkTypeOptions = await this.getList(
          "list_infra_type_log",
          this.familySpecificFields.microduct_network_type,
          false
        );
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Loads the different types of cable options and assigns them to the attribute installationTypeOptions
     */
    async loadInstallationTypeOptions() {
      try {
        this.installationTypeOptions = await this.getList(
          "list_microduct_installation_type",
          this.familySpecificFields.microduct_installation_type,
          false
        );
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Loads the different types of cable options and assigns them to the attribute implantationTypeOptions
     */
    async loadImplantationTypeOptions() {
      try {
        this.implantationTypeOptions = await this.getList(
          "list_implantation_type",
          this.familySpecificFields.microduct_pathway_type,
          false
        );
      } catch (e) {
        console.error(e);
      }
    },

    /**
     * Loads the different types of cable options and assigns them to the attribute colorProfileOptions
     */
    async loadColorProfileOptions() {
      try {
        this.colorProfileOptions = await this.getList(
          "list_microduct_color_profile",
          this.familySpecificFields.smud_microduct_color_profile,
          false
        );
      } catch (e) {
        console.error(e);
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/newComponents/StockManagement/SpecificMaterialSections/MaterialFieldsMicroDuct.vue